<template>
  <div>
    <player-breadcrumb
      class="mb-2"
      :view="$t('player_profile.exercises')"
      :player="player"
    >
      <v-row
        align="center"
        dense
      >
        <v-spacer />
        <v-col
          cols="auto"
          class="mr-2"
        >
          {{ $t('picker.date.title') }}:
        </v-col>

        <v-col cols="auto">
          <v-menu
            v-model="dateFromActive"
            :close-on-content-click="true"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="dateFromText"
                class="custom-height-text-field"
                :label="$t('date_from.label')"
                solo
                dense
                hide-details
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="dateRange.from"
              :first-day-of-week="1"
              no-title
              :max="$dayjs().tz(playerTimezone).format('YYYY-MM-DD')"
              @change="dateFromActive = false"
            />
          </v-menu>
        </v-col>
        <v-col cols="auto">
          <v-menu
            v-model="dateToActive"
            :close-on-content-click="true"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="dateToText"
                class="custom-height-text-field"
                :label="$t('date_to.label')"
                solo
                dense
                hide-details
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="dateRange.to"
              :first-day-of-week="1"
              no-title
              :min="dateRange.from"
              :max="$dayjs().tz(playerTimezone).format('YYYY-MM-DD')"
              @change="dateToActive = false"
            />
          </v-menu>
        </v-col>
      </v-row>
    </player-breadcrumb>

    <v-divider class="mt-5 mb-5" />

    <info-label :label="$t('info_label.player_profile.training_exercises')" />

    <player-training-group-card
      v-if="groups.data[0] && player.data.id"
      :player="player.data"
      :group="groups.data[0]"
      :params="strengthParams"
      :date-range="dateRange"
      type="simple"
    />
    <player-training-group-card
      v-if="groups.data[5] && player.data.id"
      :player="player.data"
      :group="groups.data[5]"
      :params="ballisticsParams"
      :date-range="dateRange"
      type="simple"
    />
    <player-training-group-card
      v-if="groups.data[7] && player.data.id"
      :player="player.data"
      :group="groups.data[7]"
      :params="mobilityParams"
      :date-range="dateRange"
      type="simple"
    />

    <player-training-group-card
      v-if="groups.data[4] && player.data.id"
      :player="player.data"
      :group="groups.data[4]"
      :params="plyometricsParams"
      :date-range="dateRange"
      type="moderate"
    />

    <player-training-group-card
      v-if="groups.data[2] && player.data.id"
      :player="player.data"
      :group="groups.data[2]"
      :params="speedParams"
      :date-range="dateRange"
      type="moderate"
    />

    <player-training-group-card
      v-if="groups.data[1] && player.data.id"
      :player="player.data"
      :group="groups.data[1]"
      :params="footballParams"
      :date-range="dateRange"
      type="moderate"
    />

    <player-training-group-card
      v-if="groups.data[6] && player.data.id"
      :player="player.data"
      :group="groups.data[6]"
      :params="cardioParams"
      :date-range="dateRange"
      type="complex"
    />

    <player-training-group-card
      v-if="groups.data[3] && player.data.id"
      :player="player.data"
      :group="groups.data[3]"
      :params="enduranceParams"
      :date-range="dateRange"
      type="complex"
    />
  </div>
</template>

<script>
import { PlayerApiService } from '@/data/services'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import { TrainingExerciseApiService } from '@/data/services/TrainingExerciseApiService'
import PlayerTrainingGroupCard from '@/components/players/PlayerTrainingGroupCard.vue'
import dayjs from 'dayjs'
import i18n from '@/plugins/i18n'
import InfoLabel from '@/components/helpers/InfoLabel.vue'
import PlayerBreadcrumb from '@/components/players/PlayerBreadcrumb.vue'
import { distanceSuffix, weightSuffix } from '@/components/training-builder/helpers'
import { getUnitSuffixForUser } from '@/utils/helpers'

export default {
  name: 'PlayerTrainingsView',
  components: {
    PlayerBreadcrumb,
    InfoLabel,
    PlayerTrainingGroupCard
  },
  mixins: [deleteConfirmationMixin],
  props: {
    dateFromTo: {
      type: Object,
      default: undefined
    }
  },
  data() {
    return {
      groups: {
        data: [],
        loading: false,
        selectedId: undefined
      },
      exercises: {
        data: [],
        loading: false,
        selected: undefined
      },
      stats: {
        data: [],
        loading: false,
        pagination: {
          current_page: 1,
          last_page: undefined,
          per_page: 20
        }
      },
      player: {
        loading: false,
        data: {}
      },
      dateRange: {
        from: undefined,
        to: undefined
      },
      dateFromActive: false,
      dateToActive: false
    }
  },
  computed: {
    dateFromText() {
      return dayjs(this.dateRange.from).format('ll')
    },
    dateToText() {
      return dayjs(this.dateRange.to).format('ll')
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    playerTimezone() {
      if (!this.user?.organisation_settings?.has_player_timezones) return undefined

      return this.player.data.settings?.timezone || undefined
    },
    strengthParams() {
      const weightUnit = weightSuffix(this.user?.organisation_settings?.unit_system)

      return [
        {
          text: this.$t('training_builder.configure.method.weight'),
          value: 'WGHT',
          unit: weightUnit,
          title: `${this.$t('training_builder.text_input.weight.title')} (${weightUnit})`
        },
        {
          text: this.$t('training_builder.configure.type.set_duration'),
          value: 'STDR',
          unit: 's',
          title: `${this.$t('picker.time.title')}(s)`
        },
        {
          text: this.$t('training_builder.text_input.iso_duration.title'),
          value: 'ISDR',
          unit: 's',
          title: `${this.$t('picker.time.title')}(s)`
        },
        {
          text: this.$t('training_builder.text_input.ex_duration.title'),
          value: 'EXDR',
          unit: 's',
          title: `${this.$t('picker.time.title')}(s)`
        },
        {
          text: this.$t('training_builder.text_input.con_duration.title'),
          value: 'CNDR',
          unit: 's',
          title: `${this.$t('picker.time.title')}(s)`
        },
        {
          text: this.$t('training_builder.text_input.ssc_duration.title'),
          value: 'SSCDR',
          unit: 's',
          title: `${this.$t('picker.time.title')}(s)`
        }
      ]
    },

    mobilityParams() {
      return [
        {
          text: this.$t('training_builder.text_input.num_of_reps.title'),
          value: 'NMRP',
          unit: 'reps',
          title: this.$t('training_builder.text_input.num_of_reps.title')
        }
      ]
    },

    ballisticsParams() {
      return [
        {
          text: this.$t('training_builder.text_input.num_of_throws.title'),
          value: 'NOTHR',
          unit: 'throws',
          title: this.$t('training_builder.text_input.num_of_throws.title')
        },
        {
          text: this.$t('training_builder.text_input.num_of_ground_contacts.title'),
          value: 'NUMGC',
          unit: 'contacts',
          title: this.$t('training_builder.text_input.num_of_ground_contacts.title')
        }
      ]
    },

    plyometricsParams() {
      const distanceUnit = distanceSuffix(this.user?.organisation_settings?.unit_system)

      return [
        {
          text: this.$t('training_builder.text_input.num_of_ground_contacts.title'),
          value: 'JMHGT',
          unit: 'contacts',
          title: this.$t('training_builder.text_input.num_of_ground_contacts.title'),
          labelUnit: distanceUnit
        }
      ]
    },

    footballParams() {
      return [
        {
          text: this.$t('training_builder.text_input.work_period_effort_level.text'),
          value: 'EFFRT',
          unit: 's',
          title: this.$t('training_builder.text_input.num_of_efforts.title'),
          labelUnit: ''
        }
      ]
    },

    speedParams() {
      const distanceUnit = distanceSuffix(this.user?.organisation_settings?.unit_system)
      const speedUnit = getUnitSuffixForUser('km/h')

      return [
        {
          text: this.$t('training_builder.groups.speed'),
          value: 'SPD',
          unit: distanceUnit,
          title: `${this.$t('training_builder.text_input.distance.title')} (${distanceUnit})`,
          labelUnit: speedUnit
        }
      ]
    },

    cardioParams() {
      const distanceUnit = distanceSuffix(this.user?.organisation_settings?.unit_system)
      const speedUnit = getUnitSuffixForUser('km/h')

      return [
        {
          text: `${this.$t('training_builder.groups.speed')}1`,
          value: 'SPD1',
          unit: distanceUnit,
          title: `${this.$t('training_builder.text_input.distance.title')} (${distanceUnit})`,
          labelUnit: speedUnit
        },
        {
          text: `${this.$t('training_builder.groups.speed')}2`,
          value: 'SPD2',
          unit: distanceUnit,
          title: `${this.$t('training_builder.text_input.distance.title')} (${distanceUnit})`,
          labelUnit: speedUnit
        },
        {
          text: `${this.$t('card_title.intensity')}1`,
          value: 'INTS1',
          unit: distanceUnit,
          title: `${this.$t('training_builder.text_input.distance.title')} (${distanceUnit})`,
          labelUnit: ''
        },
        {
          text: `${this.$t('card_title.intensity')}2`,
          value: 'INTS2',
          unit: distanceUnit,
          title: `${this.$t('training_builder.text_input.distance.title')} (${distanceUnit})`,
          labelUnit: ''
        },
        {
          text: `${this.$t('training_builder.text_input.rotation_per_min.text')}1`,
          value: this.$t('training_builder.text_input.rpm.title'),
          unit: distanceUnit,
          title: `${this.$t('training_builder.text_input.distance.title')} (${distanceUnit})`,
          labelUnit: ''
        },
        {
          text: `${this.$t('training_builder.text_input.rotation_per_min.text')}2`,
          value: this.$t('training_builder.text_input.rpm.title'),
          unit: distanceUnit,
          title: `${this.$t('training_builder.text_input.distance.title')} (${distanceUnit})`,
          labelUnit: ''
        },
        {
          text: `${this.$t('training_builder.text_input.watts.title')}1`,
          value: 'WAT1',
          unit: distanceUnit,
          title: `${this.$t('training_builder.text_input.distance.title')} (${distanceUnit})`,
          labelUnit: 'W'
        },
        {
          text: `${this.$t('training_builder.text_input.watts.title')}2`,
          value: 'WAT2',
          unit: distanceUnit,
          title: `${this.$t('training_builder.text_input.distance.title')} (${distanceUnit})`,
          labelUnit: 'W'
        },
        {
          text: `${this.$t('training_builder.text_input.incline.title')}1`,
          value: 'INCL1',
          unit: distanceUnit,
          title: `${this.$t('training_builder.text_input.distance.title')} (${distanceUnit})`,
          labelUnit: ''
        },
        {
          text: `${this.$t('training_builder.text_input.incline.title')}2`,
          value: 'INCL2',
          unit: distanceUnit,
          title: `${this.$t('training_builder.text_input.distance.title')} (${distanceUnit})`,
          labelUnit: ''
        }
      ]
    },

    enduranceParams() {
      const distanceUnit = distanceSuffix(this.user?.organisation_settings?.unit_system)
      const speedUnit = getUnitSuffixForUser('km/h')

      return [
        {
          text: `${this.$t('training_builder.groups.speed')}1`,
          value: 'SPD1',
          unit: distanceUnit,
          title: `${this.$t('training_builder.text_input.distance.title')} (${distanceUnit})`,
          labelUnit: speedUnit
        },
        {
          text: `${this.$t('training_builder.groups.speed')}2`,
          value: 'SPD2',
          unit: distanceUnit,
          title: `${this.$t('training_builder.text_input.distance.title')} (${distanceUnit})`,
          labelUnit: speedUnit
        }
      ]
    }
  },
  watch: {
    'groups.selectedId': function () {
      this.stats.data = []
      this.fetchAllExercises()
    },
    'exercises.selected': function () {
      this.fetchExerciseStats()
    },
    'stats.pagination.current_page': function () {
      this.fetchExerciseStats()
    }
  },
  created() {
    this.fetchPlayerDetails()
    this.fetchAllGroups()

    this.tableHeaders = [
      { value: 'event_at_date', text: i18n.t('picker.date.title'), sortable: false },
      { value: 'training_protocol.name', text: i18n.t('dropdown.training_protocol.title'), sortable: false },
      { value: '', text: i18n.t('add_new.exercise'), sortable: false }
    ]

    if (this.$route.query.from) {
      this.dateRange = {
        from: this.$route.query.from,
        to: this.$route.query.to
      }

      this.$router.replace({ query: {} })
    } else {
      this.dateRange.from = this.dateFromTo ? dayjs(this.dateFromTo.from).tz(this.playerTimezone).format('YYYY-MM-DD') : dayjs().tz(this.playerTimezone).subtract(2, 'week').format('YYYY-MM-DD')
      this.dateRange.to = this.dateFromTo ? dayjs(this.dateFromTo.to).tz(this.playerTimezone).format('YYYY-MM-DD') : dayjs().tz(this.playerTimezone).format('YYYY-MM-DD')
    }
  },
  methods: {
    async fetchPlayerDetails() {
      try {
        this.player.loading = true

        const resp = await PlayerApiService.getPlayerDetails(this.$route.params.id)
        this.player.data = resp.data
      } catch (e) {
        // todo
      } finally {
        this.player.loading = false
      }
    },
    async fetchAllGroups() {
      this.groups.loading = true

      try {
        this.groups.data = await TrainingExerciseApiService.getAllGroups()
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_groups.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.groups.loading = false
      }
    },
    async fetchAllExercises() {
      this.exercises.loading = true

      try {
        const result = await PlayerApiService.getTrainingExercises(this.player.data.id, {
          group_id: this.groups.selectedId || undefined
        })

        this.exercises.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_exercises.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.exercises.loading = false
      }
    },
    async fetchExerciseStats() {
      if (!this.exercises.selected) return

      this.stats.loading = true

      try {
        const result = await PlayerApiService.getTrainingExerciseStats(this.player.data.id, {
          page: this.stats.pagination.current_page,
          per_page: this.stats.pagination.per_page,
          training_exercise_id: this.exercises.selected.id,
          group_id: this.exercises?.selected?.group?.group_id
        })

        this.stats.data = result.data
        this.stats.pagination = result.meta
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_training_history.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.stats.loading = false
      }
    }
  }
}
</script>

<style scoped>

</style>
